import { useRef, useCallback, useMemo, MutableRefObject } from 'react';
import {
    createComplianceSession,
    closeComplianceSession,
    getActiveSession,
    updateComplianceSession
} from '@features/admin/api/complianceServices';
import { incrementCumulativeTime, updateLessonProgress } from '@features/courses/api/progressServices';


interface UseComplianceSessionManagerProps {
    userId: string | undefined;
    courseId: string;
    lessonId: string | null | undefined;
    ipAddress: string | undefined;
    sessionData: any;
    isActive: boolean;
    isLockedOut: boolean;
    startTimer: () => Promise<void>;
    pauseTimer: () => Promise<void>;
    stopTimer: () => Promise<void>;
    sessionTimeRef: MutableRefObject<number>;
    lessonTimeRef?: MutableRefObject<number>;
    activityTracking: any;
    lastSyncedTimeRef: MutableRefObject<number>;
    videoProgressRef: MutableRefObject<any>;
    breakHistoryRef: MutableRefObject<any[]>;
    setupPageUnloadHandler: (sessionId: string) => void;
    removePageUnloadHandler: () => void;
}

export const useComplianceSessionManager = ({
    userId,
    courseId,
    lessonId,
    ipAddress,
    sessionData,
    isActive,
    isLockedOut,
    startTimer,
    pauseTimer,
    stopTimer,
    sessionTimeRef,
    lessonTimeRef,
    activityTracking,
    lastSyncedTimeRef,
    videoProgressRef,
    breakHistoryRef,
    setupPageUnloadHandler,
    removePageUnloadHandler
}: UseComplianceSessionManagerProps) => {
    const uId = userId;
    const sessionCreationPromiseRef = useRef<Promise<void> | null>(null);

    const startSession = useCallback(async () => {
        if (sessionCreationPromiseRef.current) {
            await sessionCreationPromiseRef.current;
            return;
        }

        // Secure Resumption: If the session has already been hydrated (e.g. active break restored on mount)
        // but the master clock ticker is inactive, start the clock ticks.
        if (!isActive && !isLockedOut && sessionData.currentSessionId) {
            console.log('[ComplianceSessionManager] Resuming existing hydrated session. Activating master clock ticker.');
            startTimer();
            return;
        }

        if (!isActive && !isLockedOut && !sessionData.currentSessionId && uId && courseId) {
            sessionCreationPromiseRef.current = (async () => {
                try {
                    // 1. Check for active session first (Cross-tab collision prevention)
                    const activeSession = await getActiveSession(uId);
                    if (activeSession) {
                        const lastBreak = activeSession.breaks?.[activeSession.breaks.length - 1];
                        const hasActiveBreak = lastBreak && lastBreak.status === 'active';

                        if (hasActiveBreak) {
                            const activeSessionId = activeSession.id || '';
                            console.log(`[ComplianceSessionManager] Found active session (ID: ${activeSessionId}) on a mandatory break. Resuming it.`);
                            sessionData.createSession(activeSessionId, activeSession);
                            setupPageUnloadHandler(activeSessionId);
                            return;
                        }

                        console.warn(`[ComplianceSessionManager] Found active session on another device (ID: ${activeSession.id}). Terminating it.`);
                        const sessionIdToClose = activeSession.id || '';
                        if (sessionIdToClose) {
                            await closeComplianceSession(uId, sessionIdToClose, {
                                closureType: 'concurrent_termination',
                            });
                        }
                    }

                    const now = new Date().toISOString();
                    const session = {
                        startTime: now,
                        courseId,
                        lessonId: lessonId || null,
                        ipAddress: ipAddress || '',
                        lessonsAccessed: lessonId ? [lessonId] : [],
                        videoProgress: null
                    };

                    const sessionId = await createComplianceSession(uId, courseId, {
                        ipAddress: ipAddress || '',
                        deviceInfo: navigator.userAgent,
                        startTime: now
                    });

                    localStorage.removeItem('lastActivityTime');
                    activityTracking.resetActivity();

                    startTimer();
                    sessionData.createSession(sessionId, session);

                    if (typeof window !== 'undefined') {
                        (window as any).TIMER_READY = true;
                    }

                    setupPageUnloadHandler(sessionId);
                } catch (err) {
                    console.error('Error starting session:', err);
                } finally {
                    sessionCreationPromiseRef.current = null;
                }
            })();

            await sessionCreationPromiseRef.current;
        }
    }, [uId, courseId, lessonId, ipAddress, sessionData, isActive, isLockedOut, startTimer, activityTracking, setupPageUnloadHandler]);

    const stopSession = useCallback(async () => {
        if (isActive && sessionData.currentSessionId && uId && courseId) {
            pauseTimer();
            removePageUnloadHandler();

            try {
                const currentSessionTime = sessionTimeRef.current;
                const deltaSeconds = currentSessionTime - lastSyncedTimeRef.current;

                const activeLessons = (sessionData.lessonsAccessed || []) as string[];
                const lessonsList = lessonId ? Array.from(new Set(activeLessons.concat(lessonId))) : activeLessons;

                const timerData = {
                    duration: currentSessionTime,
                    videoProgress: videoProgressRef.current,
                    lessonsAccessed: lessonsList,
                    breaks: breakHistoryRef.current
                };

                await closeComplianceSession(uId, sessionData.currentSessionId, timerData);

                const lessonTimeVal = lessonTimeRef ? lessonTimeRef.current : 0;
                if (lessonId && lessonTimeVal > 0) {
                    await updateLessonProgress(uId, courseId, lessonId, { timeSpent: lessonTimeVal });
                }

                if (deltaSeconds > 0) {
                    await incrementCumulativeTime(uId, courseId, deltaSeconds);
                    lastSyncedTimeRef.current = currentSessionTime;
                }
            } catch (err) {
                console.error('Error stopping session:', err);
            }
        }

        stopTimer();
        sessionData.closeSession();
        lastSyncedTimeRef.current = 0;
    }, [uId, courseId, lessonId, sessionData, isActive, pauseTimer, stopTimer, sessionTimeRef, lessonTimeRef, lastSyncedTimeRef, videoProgressRef, breakHistoryRef, removePageUnloadHandler]);

    const saveSessionProgress = useCallback(async () => {
        if (uId && sessionData.currentSessionId && courseId && sessionData.currentSession) {
            try {
                const currentSessionTime = sessionTimeRef.current;
                const deltaSeconds = currentSessionTime - lastSyncedTimeRef.current;

                const activeLessons = (sessionData.lessonsAccessed || []) as string[];
                const lessonsList = lessonId ? Array.from(new Set(activeLessons.concat(lessonId))) : activeLessons;
                const currentSessionObj = (sessionData.currentSession || {}) as Record<string, any>;

                const timerData = {
                    duration: currentSessionTime,
                    videoProgress: videoProgressRef.current,
                    lessonsAccessed: lessonsList,
                    currentSession: {
                        ...currentSessionObj,
                        endTime: new Date().toISOString(),
                        duration: currentSessionTime,
                        videoProgress: videoProgressRef.current,
                        lessonsAccessed: lessonsList,
                        ipAddress: ipAddress || ''
                    }
                };

                await updateComplianceSession(uId, sessionData.currentSessionId, timerData);

                const lessonTimeVal = lessonTimeRef ? lessonTimeRef.current : 0;
                if (lessonId && lessonTimeVal > 0) {
                    await updateLessonProgress(uId, courseId, lessonId, { timeSpent: lessonTimeVal });
                }

                if (deltaSeconds > 0) {
                    await incrementCumulativeTime(uId, courseId, deltaSeconds);
                    lastSyncedTimeRef.current = currentSessionTime;
                }
            } catch (err) {
                console.error('Error saving session progress:', err);
            }
        }
    }, [uId, courseId, lessonId, ipAddress, sessionData, sessionTimeRef, lessonTimeRef, lastSyncedTimeRef, videoProgressRef, breakHistoryRef]);

    return useMemo(() => ({
        startSession,
        stopSession,
        saveSessionProgress
    }), [startSession, stopSession, saveSessionProgress]);
};
